import { ExchangeRateList } from "@/modules/exchangeRate/components";
import type { ICurrencyExchangeForm } from "@/modules/transaction/common/types";
import { CurrencyExchangeForm } from "@/modules/transaction/components";
import { currencyExchangeValidationSchema } from "@/modules/transaction/validations";
import { CloudflareAsset, SideSheetPosition, StaticUrl, Storage, } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader, } from "@/shared/components";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

export const CurrencyExchange = ({ isCopy, isEdit }: IOperationPageProps) => {
  const isDesktop = useMediaQuery();
  const { t, exists } = useTranslationExists();
  const [isInfoSideBarOpen, setIsInfoSideBarOpen] = useState<boolean>(false);
  const [isCurrencySideBarOpen, setIsCurrencySideBarOpen] = useState<boolean>(false);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);

  const initialValues: ICurrencyExchangeForm = {
    transactionNumber: "",
    creditAccount: "",
    debitAccount: "",
    amount: "",
    rate: "",
    isRateChangeAcceptable: false,
    tin: localStorage.getItem(Storage.Tin) || "",
    purpose: "",
    preferentialRate: null,
    isSaveAsTemplate: false,
    templateName: "",
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={currencyExchangeValidationSchema({ t, isNameUnique })}
      >
        <PageHeader>
          {!isInfoSideBarOpen && !isCurrencySideBarOpen && (
            <>
              {exists("transfer.sidebarTitle") && (
                <Button
                  type="secondary"
                  size="small"
                  className="info-side-sheet-btn"
                  data-testid="open-info-side-sheet"
                  data-ga-id="open-info-side-sheet"
                  data-ga-action="Open Currency exchange info side sheet"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                    ),
                    alignment: "left",
                  }}
                  onClick={() => setIsInfoSideBarOpen(!isInfoSideBarOpen)}
                />
              )}
              <Button
                type="secondary"
                size="small"
                className="ml-16 currencies-side-sheet-btn"
                data-testid="open-info-side-sheet"
                data-ga-id="open-info-side-sheet"
                data-ga-action="Open Currency exchange list side sheet"
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.Currencies}/>
                  ),
                  alignment: "left",
                }}
                onClick={() => {
                  setIsCurrencySideBarOpen(!isCurrencySideBarOpen);
                }}
              />
            </>
          )}
        </PageHeader>
        <CurrencyExchangeForm
          isCopy={isCopy}
          isEdit={isEdit}
          isNameUnique={isNameUnique}
          setIsNameUnique={setIsNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isInfoSideBarOpen}
        className="info-side-sheet"
        onClose={() => setIsInfoSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.currencyExchange.sidebarMessage")}
          </Text>
          <InfoLinks keys={[StaticUrl.CorporateTariffs, StaticUrl.CardTariffs]}/>
        </div>
      </SideSheet>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isWithDivider
        isOpen={isCurrencySideBarOpen}
        className="currencies-side-sheet"
        onClose={() => setIsCurrencySideBarOpen(false)}
        title={t("transfer.exchangeRateSidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <ExchangeRateList isSideSheetOpen={isCurrencySideBarOpen}/>
        </div>
      </SideSheet>
    </>
  );
};
